import { useState } from "react";
import type { Role } from "@amber/contracts";
import {
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  RadioCards,
  ReasonField,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import { ROLE_LABELS, type UserRow } from "../../contracts/index";
import { accessCommands } from "../queries";
import { AFTER_ACCESS_CHANGE } from "../keys";
import { ROLE_OPTIONS } from "./shared";

export function RoleDialog({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const toast = useToast();
  const [role, setRole] = useState<Role>(user.role);
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      accessCommands.changeRole(user.id, { role, base_revision: user.revision, reason: text }, csrf),
    invalidate: AFTER_ACCESS_CHANGE,
    onSuccess: () => {
      toast.show(`${user.display_name} is now ${ROLE_LABELS[role]}`);
      onClose();
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const unchanged = role === user.role;
  const submit = () => {
    if (reason.submit() && !unchanged) mutation.mutate(reason.trimmed);
  };
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Change the role of ${user.display_name}`}
      onSubmit={submit}
      footer={
        <DialogFooter onClose={onClose} confirm="Change role" pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      <DialogText>
        Currently <b>{ROLE_LABELS[user.role]}</b>. The new role applies at once, also in their open sessions.
      </DialogText>
      <RadioCards
        label="Role"
        value={role}
        options={ROLE_OPTIONS}
        onChange={setRole}
        error={reason.submitted && unchanged ? "Choose a different role" : (fieldErrors["role"] ?? null)}
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}
